import { useCallback, useMemo, useState } from "react"
import { Line } from "react-chartjs-2"
import { Chart as ChartJS, Filler } from "chart.js"

import { HoveredIndex } from "../../trends/streams-daily/StreamsDaily"
import { capitalize, convertToDashedDate, withAlpha } from "../../../../utility"
import { TrendTrack } from "../../../../types"
import { colorPaletteOptions, platformColorPalette } from "../../../../constants"
import { lang } from "../../../../constants/languages"
import { KSpan, KTitleSpan } from "kahuna-base-react-components"
import { DateRangeType } from "kahuna-base-react-components/dist/components/KSelectRange/KSelectRange"

ChartJS.register(Filler)

export const DailyPerformanceChart = ({
  data,
  selectedIndex,
  dateValues,
  platform,
  dataKey,
  title
}: {
  data: { [key: string]: { [key: string]: number } }
  selectedIndex: number
  dateValues: DateRangeType
  platform: string
  dataKey: string
  title?: string
}) => {
  const [hoveredIndex, setHoveredIndex] = useState<HoveredIndex>({
    datasetIndex: -1,
    dataIndex: -1
  })

  const handleHover = useCallback((_evt: unknown, elements: any[]) => {
    setHoveredIndex((prev: HoveredIndex) => {
      if (elements.length) {
        const { datasetIndex, index: dataIndex } = elements[0]
        if (datasetIndex === prev.datasetIndex && dataIndex === prev.dataIndex) return prev
        return { datasetIndex, dataIndex }
      }
      if (prev.datasetIndex === -1 && prev.dataIndex === -1) return prev
      return { datasetIndex: -1, dataIndex: -1 }
    })
  }, [])

  const baseChart = useMemo(() => {
    const dateStreamMap: Record<string, number> = {}
    Object.keys(data).forEach((key: string) => {
      // key is date, value is object => object {"dsp": value, "download": value, socialConsumption:value,  socialConsumption: value}
      const date = convertToDashedDate(key)
      const valueKey = dataKey || "dsp"
      const streamValue = data[key][valueKey] ?? 0
      if (!dateStreamMap[date]) dateStreamMap[date] = 0
      dateStreamMap[date] += streamValue
    })

    const labels = Object.keys(dateStreamMap).sort()
    const points = labels.map((label) => dateStreamMap[label] ?? 0)
    const isSinglePoint = labels.length === 1
    const capitalizedPlatform = capitalize(platform ?? "-")
    const lineColor = (platformColorPalette as any)[capitalizedPlatform] || colorPaletteOptions[selectedIndex]
    const datasets: any[] = [
      {
        label: capitalizedPlatform,
        data: points,
        borderColor: lineColor,
        borderWidth: 2,
        tension: 0.4,

        fill: "origin" as const,
        backgroundColor: (ctx: any) => {
          const chart = ctx.chart
          const { ctx: c, chartArea } = chart

          if (!chartArea) return withAlpha(lineColor, 0.1)

          const g = c.createLinearGradient(0, chartArea.top, 0, chartArea.bottom)
          g.addColorStop(0, withAlpha(lineColor, 0.22))
          g.addColorStop(0.75, withAlpha(lineColor, 0.06))
          g.addColorStop(1, withAlpha(lineColor, 0.0))
          return g
        },

        pointBorderColor: "transparent",
        pointRadius: isSinglePoint ? 5 : 0,
        pointHoverRadius: 4,
        pointHitRadius: 12
      }
    ]
    return { labels, datasets }
  }, [data])

  const chartData = useMemo(() => {
    return {
      labels: baseChart.labels,
      datasets: baseChart.datasets.map((ds: any, i: number) => {
        const isOtherHovered = !(hoveredIndex.datasetIndex === i || hoveredIndex.datasetIndex === -1)
        const lineColor = ds.borderColor
        return {
          ...ds,
          borderColor: lineColor,
          backgroundColor: ds.backgroundColor
        }
      })
    }
  }, [baseChart, hoveredIndex])

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      clip: false as const,

      interaction: {
        intersect: false,
        mode: "nearest" as const
      },

      plugins: {
        datalabels: { display: false },

        tooltip: {
          displayColors: false,
          backgroundColor: "#333",
          xAlign: "center" as const,
          yAlign: "bottom" as const,
          caretSize: 0,
          caretPadding: 10,
          padding: 10,
          titleFont: { family: "Inter", weight: 600, size: 13, lineHeight: "16px" },
          bodyFont: { family: "Inter", weight: 400, size: 12, lineHeight: "16px" }
        },

        hover: { intersect: false },

        legend: {
          display: true,
          position: "top" as const,
          align: "end" as const,
          labels: {
            font: { family: "Inter", weight: 500, size: 13, lineHeight: "13px" },
            usePointStyle: true,
            pointStyle: "circle",
            boxWidth: 8,
            boxHeight: 8,
            generateLabels: (chart: any) => {
              const datasets = chart.data.datasets
              return datasets.map((dataset: any, i: number) => ({
                text: dataset.label,
                fillStyle: dataset.borderColor,
                strokeStyle: dataset.borderColor,
                lineWidth: 0,
                hidden: !chart.isDatasetVisible(i),
                datasetIndex: i,
                fontColor: "#666"
              }))
            }
          }
        }
      },

      scales: {
        y: {
          grid: {
            display: false,
            drawTicks: true
          },
          border: { display: true, dash: [3, 3] },
          ticks: {
            color: "#999",
            font: { family: "Inter", weight: 500, size: 11, lineHeight: "12px" }
          }
        },
        x: {
          grid: {
            display: true,
            drawBorder: true,
            drawTicks: true,
            drawOnChartArea: false
          },
          border: { display: true },
          ticks: {
            color: "#999",
            font: { family: "Inter", weight: 500, size: 11, lineHeight: "12px" }
          }
        }
      },

      onHover: handleHover
    }),
    [handleHover]
  )

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        padding: 10,
        borderRadius: "10px",
        border: "1px solid var(--stroke-soft-200, #F3F3F3)",
        background: " var(--bg-white-0, #FFF)",
        boxShadow: "0px 2px 4px 0px rgba(27, 28, 29, 0.04)"
      }}
    >
      <div style={{ width: "100%", height: "5%", paddingLeft: 12, paddingTop: 4 }}>
        <KTitleSpan text={title || lang.trends.headers.daily_streams} fontSize={16} color="#737373" lineHeight="20px" fontWeight={500} />
      </div>
      <div style={{ width: "100%", height: "95%", padding: 20 }}>
        <Line data={chartData} options={options} style={{ width: "100%", height: "100%" }} />
      </div>
    </div>
  )
}
